/**
 * Figma API 数据获取模块
 */

import { v4 as uuidv4 } from 'uuid';
import type { FigmaFile, FigmaNode } from '../types';
import { Logger } from '../utils/logger';
import { figmaFileSchema } from '../schemas';

// Figma API 配置
interface FigmaApiConfig {
  accessToken?: string;
  baseUrl?: string;
  timeout?: number;
  traceId?: string;
}

// 默认配置
const DEFAULT_CONFIG: Required<FigmaApiConfig> = {
  accessToken: '',
  baseUrl: 'https://api.figma.com/v1',
  timeout: 30000,
  traceId: '',
};

// API 响应类型
interface FigmaApiResponse {
  name: string;
  lastModified: string;
  thumbnailUrl: string;
  version: string;
  role: string;
  editorType: string;
  linkAccess: string;
  document: FigmaNode;
  components: Record<string, any>;
  componentSets: Record<string, any>;
  schemaVersion: number;
  styles: Record<string, any>;
}

/**
 * Figma 数据获取器
 */
export class FigmaDataFetcher {
  private config: Required<FigmaApiConfig>;
  private logger: Logger;

  constructor(config: FigmaApiConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.logger = new Logger({ traceId: this.config.traceId || uuidv4() });
  }

  /**
   * 设置访问令牌
   */
  setAccessToken(token: string): void {
    this.config.accessToken = token;
  }

  /**
   * 获取 Figma 文件数据
   */
  async fetchFile(fileKey: string, nodeId?: string): Promise<FigmaFile> {
    const startTime = Date.now();
    const url = this.buildUrl(`/files/${fileKey}`, nodeId ? { ids: nodeId } : undefined);
    
    this.logger.figmaApiCall('GET', url, fileKey);

    try {
      const response = await this.makeRequest(url);
      const duration = Date.now() - startTime;
      
      this.logger.figmaApiResponse('GET', url, 200, duration);
      
      // 转换 API 响应为内部格式
      const figmaFile = this.transformApiResponse(response, fileKey);
      
      // 验证数据格式
      const validatedFile = figmaFileSchema.parse(figmaFile);
      
      this.logger.info(`成功获取 Figma 文件数据`, {
        fileKey,
        nodeCount: this.countNodes(validatedFile.nodes),
        componentCount: Object.keys(validatedFile.components).length,
        duration,
      });

      return validatedFile;
    } catch (error) {
      const duration = Date.now() - startTime;
      this.logger.error(`获取 Figma 文件失败`, {
        fileKey,
        nodeId,
        error: error instanceof Error ? error.message : String(error),
        duration,
      });
      throw error;
    }
  }

  /**
   * 获取特定节点数据
   */
  async fetchNodes(fileKey: string, nodeIds: string[]): Promise<Record<string, FigmaNode>> {
    const startTime = Date.now();
    const url = this.buildUrl(`/files/${fileKey}/nodes`, { 
      ids: nodeIds.join(',') 
    });
    
    this.logger.figmaApiCall('GET', url, fileKey);

    try {
      const response = await this.makeRequest(url);
      const duration = Date.now() - startTime;
      
      this.logger.figmaApiResponse('GET', url, 200, duration);
      
      this.logger.info(`成功获取节点数据`, {
        fileKey,
        nodeIds,
        nodeCount: nodeIds.length,
        duration,
      });

      return response.nodes || {};
    } catch (error) {
      const duration = Date.now() - startTime;
      this.logger.error(`获取节点数据失败`, {
        fileKey,
        nodeIds,
        error: error instanceof Error ? error.message : String(error),
        duration,
      });
      throw error;
    }
  }

  /**
   * 获取文件版本信息
   */
  async fetchFileVersions(fileKey: string): Promise<any[]> {
    const startTime = Date.now();
    const url = this.buildUrl(`/files/${fileKey}/versions`);
    
    this.logger.figmaApiCall('GET', url, fileKey);

    try {
      const response = await this.makeRequest(url);
      const duration = Date.now() - startTime;
      
      this.logger.figmaApiResponse('GET', url, 200, duration);
      
      this.logger.info(`成功获取版本信息`, {
        fileKey,
        versionCount: response.versions?.length || 0,
        duration,
      });

      return response.versions || [];
    } catch (error) {
      const duration = Date.now() - startTime;
      this.logger.error(`获取版本信息失败`, {
        fileKey,
        error: error instanceof Error ? error.message : String(error),
        duration,
      });
      throw error;
    }
  }

  /**
   * 构建 API URL
   */
  private buildUrl(path: string, params?: Record<string, string>): string {
    const url = new URL(path, this.config.baseUrl);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          url.searchParams.append(key, value);
        }
      });
    }
    
    return url.toString();
  }

  /**
   * 发起 HTTP 请求
   */
  private async makeRequest(url: string): Promise<any> {
    if (!this.config.accessToken) {
      throw new Error('Figma access token is required');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Figma-Token': this.config.accessToken,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Figma API error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Request timeout after ${this.config.timeout}ms`);
      }
      
      throw error;
    }
  }

  /**
   * 转换 API 响应为内部格式
   */
  private transformApiResponse(response: FigmaApiResponse, fileKey: string): FigmaFile {
    return {
      name: response.name,
      metadata: {
        lastModified: response.lastModified,
        version: response.version,
        fileKey,
        schemaVersion: response.schemaVersion,
      },
      components: response.components || {},
      componentSets: response.componentSets || {},
      nodes: response.document ? [response.document] : [],
    };
  }

  /**
   * 递归计算节点数量
   */
  private countNodes(nodes: FigmaNode[]): number {
    let count = nodes.length;
    
    for (const node of nodes) {
      if (node.children) {
        count += this.countNodes(node.children);
      }
    }
    
    return count;
  }

  /**
   * 获取当前 traceId
   */
  getTraceId(): string {
    return this.logger.getTraceId();
  }

  /**
   * 设置新的 traceId
   */
  setTraceId(traceId: string): void {
    this.logger.setTraceId(traceId);
    this.config.traceId = traceId;
  }
}

// 便捷函数
export function createFigmaDataFetcher(config?: FigmaApiConfig): FigmaDataFetcher {
  return new FigmaDataFetcher(config);
}